import { projectId, publicAnonKey } from '/utils/supabase/info';
import { getAuthHeadersForProtectedApi } from './db-storage';
import { createCorrelationId, trackTelemetryEvent } from './telemetry';
import type {
  EnterpriseOutcomeBridgeStored,
  OutcomeAttributionPreview,
  OutcomeBridgeSnapshotRecord,
} from './enterprise-wave4-outcome';
import type { MarketplaceConnectorRollout } from './enterprise-marketplace-catalog';
import type { EnterpriseCarrierPathwayProfile } from './enterprise-carrier-pathways';
import type { EnterpriseRegionPostureProfile } from './enterprise-region-posture';
import type { EnterpriseTelemetryBaselinesProfile } from './enterprise-telemetry-baselines';
import type { EnterpriseCostQuotaProfile } from './enterprise-cost-quota';
import type { EnterpriseAccountGrowthPrioritiesProfile } from './enterprise-account-growth-priorities';

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4`;

/** Legacy admin session (/admin/secure-login) or unified Supabase admin JWT (no session). */
function adminEnterpriseHeaders(adminSessionToken?: string | null): Record<string, string> {
  const trimmed = typeof adminSessionToken === 'string' ? adminSessionToken.trim() : '';
  if (trimmed.length > 0) {
    return withJsonHeaders(undefined, trimmed);
  }
  return {
    'Content-Type': 'application/json',
    ...getAuthHeadersForProtectedApi(),
  };
}

function withJsonHeaders(accessToken?: string, adminSessionToken?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${publicAnonKey}`,
    apikey: publicAnonKey,
  };
  if (accessToken) headers['X-User-Token'] = accessToken;
  if (adminSessionToken) headers['X-Session-Token'] = adminSessionToken;
  return headers;
}

function withEnterpriseAccessHeaders(accessToken?: string): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${publicAnonKey}`,
    apikey: publicAnonKey,
  };
  if (accessToken) headers['X-Enterprise-Access-Token'] = accessToken;
  return headers;
}

/** Admin GETs that return file bodies (no JSON Content-Type). */
function adminEnterpriseDownloadHeaders(adminSessionToken?: string | null): Record<string, string> {
  const trimmed = typeof adminSessionToken === 'string' ? adminSessionToken.trim() : '';
  if (trimmed.length > 0) {
    return {
      Authorization: `Bearer ${publicAnonKey}`,
      apikey: publicAnonKey,
      'X-Session-Token': trimmed,
    };
  }
  return getAuthHeadersForProtectedApi();
}

/**
 * Download board pack (metrics, segment alerts, intervention queue) as JSON, CSV, or PDF.
 * Triggers a browser download; filename comes from Content-Disposition when present.
 */
export async function downloadEnterpriseBoardPack(
  orgId: string,
  adminSessionToken: string | null,
  format: 'json' | 'csv' | 'pdf',
): Promise<void> {
  const url = `${BASE}/admin/enterprise/board-pack?orgId=${encodeURIComponent(orgId)}&format=${encodeURIComponent(format)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: adminEnterpriseDownloadHeaders(adminSessionToken),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || res.statusText);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition');
  let filename =
    format === 'csv' ? 'tension-board-pack.csv' : format === 'pdf' ? 'tension-board-pack.pdf' : 'tension-board-pack.json';
  const m = cd && /filename="([^"]+)"/.exec(cd);
  if (m?.[1]) filename = m[1];
  const a = document.createElement('a');
  const href = URL.createObjectURL(blob);
  a.href = href;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

export type EnterpriseCohortBuckets = {
  stable: number;
  elastic: number;
  highTension: number;
};

export type EnterpriseOverview = {
  activeMembers: number;
  checkInsLast30d: number;
  avgRtiLast30d: number | null;
  highTensionPctLast30d: number;
  trendDelta14d: number | null;
  forecastRisk72h: number | null;
  cohorts: EnterpriseCohortBuckets;
  /** RTI cohort counts in the most recent 14 days (within loaded window). */
  cohortsLast14d?: EnterpriseCohortBuckets;
  /** RTI cohort counts in the prior 14 days (days 15–28 of the 30d window). */
  cohortsPrior14d?: EnterpriseCohortBuckets;
  /** Movement in cohort counts: last 14d minus prior 14d. */
  cohortDelta14d?: EnterpriseCohortBuckets;
};

export type EnterpriseTenantSettings = {
  organization: {
    id: string;
    name: string;
    slug: string;
    status: string;
    industry?: string | null;
    sizeBand?: string | null;
  };
  branding: {
    supportEmail?: string | null;
    customDomain?: string | null;
    primaryColor?: string | null;
    accentColor?: string | null;
    brandName?: string | null;
  } | null;
  settings: {
    seatLimit?: number | null;
    workspaceBadgeEnabled?: boolean;
    domainVerified?: boolean;
    allowedEmailDomains?: Array<string>;
    /** Resolved defaults + KV overrides for cohort/population alert thresholds. */
    segmentAlertPolicy?: EnterpriseSegmentAlertPolicy;
    /** Response SLO target for high-tension queue handling (hours). */
    responseSlaHours?: number;
    /** Optional override for segment/board-pack digest emails (else branding support email). */
    segmentAlertDigestEmail?: string | null;
    /** ISO date (YYYY-MM-DD) for renewal-window signals; stored in tenant KV. */
    contractRenewalDate?: string | null;
    /** HTTPS URL for outbound partner workflow POSTs (ticketing/WMS). */
    partnerWorkflowWebhookUrl?: string | null;
    partnerWebhookMinSeverity?: 'critical' | 'warning';
    /** Whether a signing secret is stored in tenant KV (value is never returned). */
    partnerWorkflowSigningSecretSet?: boolean;
    /** HTTPS URL for SLA / dead-letter paging (PagerDuty, Slack, etc.). */
    partnerOpsAlertWebhookUrl?: string | null;
    partnerOpsAlertSigningSecretSet?: boolean;
    /** Email for partner integration SLA alerts (Resend; requires RESEND_API_KEY on server). */
    partnerSloAlertEmail?: string | null;
    /** Alert when 7d success rate falls below this (0–100); default 85 if unset. */
    partnerSloAlertMinSuccessPct?: number | null;
  };
  seatSummary: {
    active: number;
    pending: number;
    suspended: number;
  };
  /** DNS TXT workflow for custom domain; operator toggle still available on settings.domainVerified. */
  domainVerification?: EnterpriseDomainVerificationInfo;
};

export type EnterpriseDomainVerificationInfo = {
  state: 'none' | 'ready' | 'stale';
  currentDomainHost: string | null;
  verificationDomainHost?: string;
  txtHost: string | null;
  txtRecordValue: string | null;
  tokenIssuedAt: string | null;
  lastCheckedAt: string | null;
  lastCheckMatched: boolean | null;
  lastCheckError: string | null;
  tokenStale: boolean;
};

export type EnterpriseSeatMember = {
  userId: string;
  role: string;
  status: string;
  createdAt: string | null;
};

export type EnterpriseSeatEngagementSignal = {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  detail: string;
  count: number;
};

export type EnterpriseRenewalExpansionSignal = EnterpriseSeatEngagementSignal;

export type EnterpriseSeatEngagementSummary = {
  active: number;
  pending: number;
  suspended: number;
  seatLimit: number | null;
  totalMemberships: number;
};

export type EnterpriseRenewalExpansionSummary = {
  orgStatus: string;
  active: number;
  seatLimit: number | null;
  utilizationPct: number | null;
  checkInsRecent30: number;
  checkInsPrior30: number;
  usageVelocity: number | null;
  segmentAlertCount: number;
  criticalSegmentAlerts: number;
  contractRenewalDate: string | null;
  daysToContractRenewal: number | null;
};

export type EnterpriseOrgMembership = {
  role: string;
  status: string;
  organizations: {
    id: string;
    name: string;
    slug: string;
    status: string;
    industry?: string | null;
    size_band?: string | null;
    created_at: string;
  };
};

/** Per-org segment alert thresholds (optional overrides stored in tenant KV). */
export type EnterpriseSegmentAlertPolicy = {
  highTensionCohortDeltaMin: number;
  stableCohortDeclineMin: number;
  elasticCohortSurgeMin: number;
  highTensionPctMin: number;
  trendDeltaMin: number;
};

export type EnterpriseSegmentAlertItem = {
  id: string;
  severity: 'warning' | 'critical';
  title: string;
  detail: string;
};

export type EnterpriseAccessValidation = {
  success: boolean;
  valid?: boolean;
  expiresAt?: string;
  error?: string;
};

export async function verifyEnterpriseAccessPassword(
  password: string,
): Promise<{ success: boolean; token?: string; expiresAt?: string; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/access/verify`, {
      method: 'POST',
      headers: withJsonHeaders(),
      body: JSON.stringify({ password }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      token: (body as { token?: string }).token,
      expiresAt: (body as { expiresAt?: string }).expiresAt,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function validateEnterpriseAccessToken(token: string): Promise<EnterpriseAccessValidation> {
  try {
    const res = await fetch(`${BASE}/enterprise/access/validate`, {
      headers: withEnterpriseAccessHeaders(token),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        valid: false,
        error: (body as { error?: string }).error || res.statusText,
      };
    }
    return {
      success: true,
      valid: Boolean((body as { valid?: boolean }).valid),
      expiresAt: (body as { expiresAt?: string }).expiresAt,
    };
  } catch (error) {
    return { success: false, valid: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function revokeEnterpriseAccessToken(token: string): Promise<void> {
  try {
    await fetch(`${BASE}/enterprise/access/logout`, {
      method: 'POST',
      headers: withEnterpriseAccessHeaders(token),
    });
  } catch {
    // Ignore logout failures.
  }
}

export async function persistServerCheckIn(
  payload: {
    relationshipId: string | null;
    relationshipDomain?: 'romantic' | 'friendship' | 'family' | 'leadership';
    rtiScore: number;
    emotionalAverage?: number;
    components: Record<string, number>;
    triggers: string[];
    metadata?: Record<string, unknown>;
    organizationId?: string | null;
  },
  accessToken?: string | null,
): Promise<{ success: boolean; checkIn?: { id: string; occurred_at: string }; error?: string }> {
  if (!accessToken) {
    void trackTelemetryEvent('checkin_submit_failed', {
      reason: 'no_access_token',
      source: 'enterprise_api',
    });
    return { success: false, error: 'no access token' };
  }
  const startedAt = performance.now();
  const correlationId = createCorrelationId('checkin');
  try {
    const res = await fetch(`${BASE}/checkins`, {
      method: 'POST',
      headers: {
        ...withJsonHeaders(accessToken),
        'X-Correlation-Id': correlationId,
      },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      void trackTelemetryEvent('checkin_submit_failed', {
        source: 'enterprise_api',
        correlation_id: correlationId,
        status: res.status,
        duration_ms: Math.round(performance.now() - startedAt),
      });
      return { success: false, error: (body as { error?: string }).error || res.statusText };
    }
    void trackTelemetryEvent('checkin_submitted', {
      source: 'enterprise_api',
      correlation_id: correlationId,
      duration_ms: Math.round(performance.now() - startedAt),
      has_organization_id: Boolean(payload.organizationId),
      relationship_domain: payload.relationshipDomain ?? 'romantic',
    });
    return {
      success: true,
      checkIn: (body as { checkIn?: { id: string; occurred_at: string } }).checkIn,
    };
  } catch (error) {
    void trackTelemetryEvent('checkin_submit_failed', {
      source: 'enterprise_api',
      correlation_id: correlationId,
      duration_ms: Math.round(performance.now() - startedAt),
      reason: error instanceof Error ? error.message : String(error),
    });
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseServerCheckIn = {
  id: string;
  organization_id: string | null;
  relationship_id: string | null;
  relationship_domain: 'romantic' | 'friendship' | 'family' | 'leadership';
  rti_score: number;
  emotional_average: number | null;
  components: Record<string, number>;
  triggers: string[];
  metadata: Record<string, unknown> | null;
  occurred_at: string;
};

export async function listServerCheckIns(
  accessToken: string,
  options?: { relationshipId?: string | null; organizationId?: string | null; limit?: number },
): Promise<{ success: boolean; checkIns?: EnterpriseServerCheckIn[]; error?: string }> {
  try {
    const params = new URLSearchParams();
    const limit = options?.limit ?? 120;
    params.set('limit', String(limit));
    if (options?.relationshipId) params.set('relationshipId', options.relationshipId);
    if (options?.organizationId) params.set('organizationId', options.organizationId);
    const res = await fetch(`${BASE}/checkins?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      checkIns: (body as { checkIns?: EnterpriseServerCheckIn[] }).checkIns || [],
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseWorkspaceTimelineRow = {
  id: string;
  user_id: string;
  organization_id: string;
  relationship_domain: 'romantic' | 'friendship' | 'family' | 'leadership';
  rti_score: number;
  emotional_average: number | null;
  triggers: string[];
  metadata: Record<string, unknown> | null;
  occurred_at: string;
};

export async function fetchEnterpriseWorkspaceTimeline(
  accessToken: string,
  orgId: string,
  limit = 180,
): Promise<{ success: boolean; scope?: 'organization' | 'self'; timeline?: EnterpriseWorkspaceTimelineRow[]; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    params.set('limit', String(limit));
    const res = await fetch(`${BASE}/enterprise/workspace/timeline?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      scope: (body as { scope?: 'organization' | 'self' }).scope,
      timeline: (body as { timeline?: EnterpriseWorkspaceTimelineRow[] }).timeline || [],
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseWorkspaceInsights = {
  sampleDays: number;
  totalCheckIns: number;
  avgRti: number;
  highCount: number;
  highRatePct: number;
  last7d: number;
  leadershipAvg: number | null;
};

export async function fetchEnterpriseWorkspaceInsights(
  accessToken: string,
  orgId: string,
  days = 90,
): Promise<{
  success: boolean;
  scope?: 'organization' | 'self';
  summary?: EnterpriseWorkspaceInsights;
  domainCounts?: Record<string, number>;
  error?: string;
}> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    params.set('days', String(days));
    const res = await fetch(`${BASE}/enterprise/workspace/insights?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      scope: (body as { scope?: 'organization' | 'self' }).scope,
      summary: (body as { summary?: EnterpriseWorkspaceInsights }).summary,
      domainCounts: (body as { domainCounts?: Record<string, number> }).domainCounts || {},
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseWorkspaceInterventionRow = {
  id: string;
  userId: string;
  relationshipDomain: 'romantic' | 'friendship' | 'family' | 'leadership';
  rtiScore: number;
  severity: 'critical' | 'high' | 'elevated';
  entityLabel: string | null;
  primaryTrigger: string | null;
  occurredAt: string;
  recommendation: string;
};

export async function fetchEnterpriseWorkspaceInterventions(
  accessToken: string,
  orgId: string,
  days = 30,
): Promise<{
  success: boolean;
  scope?: 'organization' | 'self';
  role?: string;
  roleAction?: string;
  windowDays?: number;
  responseSlaHours?: number;
  slaBreaches?: number;
  queue?: EnterpriseWorkspaceInterventionRow[];
  error?: string;
}> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    params.set('days', String(days));
    const res = await fetch(`${BASE}/enterprise/workspace/interventions?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const parsed = body as {
      scope?: 'organization' | 'self';
      role?: string;
      roleAction?: string;
      windowDays?: number;
      queue?: EnterpriseWorkspaceInterventionRow[];
    };
    return {
      success: true,
      scope: parsed.scope,
      role: parsed.role,
      roleAction: parsed.roleAction,
      windowDays: parsed.windowDays,
      responseSlaHours: typeof (body as { responseSlaHours?: number }).responseSlaHours === 'number'
        ? (body as { responseSlaHours: number }).responseSlaHours
        : undefined,
      slaBreaches: typeof (body as { slaBreaches?: number }).slaBreaches === 'number'
        ? (body as { slaBreaches: number }).slaBreaches
        : undefined,
      queue: parsed.queue || [],
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseWorkspaceAnalytics = {
  scope: 'organization' | 'self';
  role: string;
  windows: {
    count7: number;
    count30: number;
    count90: number;
    avgRti7: number;
    avgRti30: number;
    avgRti90: number;
    highRate7: number;
    highRate30: number;
    highRate90: number;
    trendDelta7v30: number;
  };
  domainBreakdown30: Record<string, number>;
  topTriggers30: Array<{ trigger: string; count: number }>;
  interventionLoad30: { critical: number; high: number; elevated: number };
  responseSlaHours: number;
  slaBreaches30: number;
  queueAging30: { under24h: number; between24hTo72h: number; over72h: number };
};

export async function fetchEnterpriseWorkspaceAnalytics(
  accessToken: string,
  orgId: string,
): Promise<{ success: boolean; analytics?: EnterpriseWorkspaceAnalytics; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    const res = await fetch(`${BASE}/enterprise/workspace/analytics?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      analytics: body as EnterpriseWorkspaceAnalytics,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseRenewalExpansionWorkspacePreview = {
  generatedAt: string;
  summary: EnterpriseRenewalExpansionSummary;
  signals: EnterpriseRenewalExpansionSignal[];
};

export type EnterpriseWorkspaceQbrExcerptsResponse = {
  visibility: 'full' | 'partial';
  updatedAt: string | null;
  excerpts: EnterpriseAccountGrowthPrioritiesProfile;
};

/** Wave 9 account-growth KV notes — owner/admin see all fields; analyst/hr/manager see renewal + QBR cadence only. */
export async function fetchEnterpriseWorkspaceQbrExcerpts(
  accessToken: string,
  orgId: string,
): Promise<{ success: boolean; data?: EnterpriseWorkspaceQbrExcerptsResponse; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    const res = await fetch(`${BASE}/enterprise/workspace/qbr-excerpts?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const data = body as EnterpriseWorkspaceQbrExcerptsResponse & { success?: boolean };
    if (!data.excerpts) {
      return { success: false, error: 'Invalid QBR excerpts response' };
    }
    return {
      success: true,
      data: {
        visibility: data.visibility === 'partial' ? 'partial' : 'full',
        updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : null,
        excerpts: data.excerpts,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/** Same renewal/expansion engine as Command Center — requires owner, admin, or analyst org role. */
export async function fetchEnterpriseWorkspaceRenewalPreview(
  accessToken: string,
  orgId: string,
): Promise<{ success: boolean; preview?: EnterpriseRenewalExpansionWorkspacePreview; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    const res = await fetch(`${BASE}/enterprise/workspace/renewal-preview?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const preview = body as EnterpriseRenewalExpansionWorkspacePreview & { success?: boolean };
    if (!preview.generatedAt || !preview.summary) {
      return { success: false, error: 'Invalid renewal preview response' };
    }
    return {
      success: true,
      preview: {
        generatedAt: preview.generatedAt,
        summary: preview.summary,
        signals: Array.isArray(preview.signals) ? preview.signals : [],
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseWorkspacePolicy = {
  segmentAlertPolicy: EnterpriseSegmentAlertPolicy;
  responseSlaHours: number;
};

export async function fetchEnterpriseWorkspacePolicy(
  accessToken: string,
  orgId: string,
): Promise<{ success: boolean; canEdit?: boolean; policy?: EnterpriseWorkspacePolicy; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    const res = await fetch(`${BASE}/enterprise/workspace/policy?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const parsed = body as { canEdit?: boolean; policy?: EnterpriseWorkspacePolicy };
    return { success: true, canEdit: Boolean(parsed.canEdit), policy: parsed.policy };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseWorkspacePolicy(
  accessToken: string,
  payload: { organizationId: string; segmentAlertPolicy?: Partial<EnterpriseSegmentAlertPolicy>; responseSlaHours?: number },
): Promise<{ success: boolean; policy?: EnterpriseWorkspacePolicy; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/workspace/policy`, {
      method: 'POST',
      headers: withJsonHeaders(accessToken),
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const parsed = body as { policy?: EnterpriseWorkspacePolicy };
    return { success: true, policy: parsed.policy };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseWorkspaceObservability = {
  scope: 'organization' | 'self';
  role: string;
  policy: {
    responseSlaHours: number;
    segmentAlertPolicy: EnterpriseSegmentAlertPolicy;
  };
  scorecard: {
    riskScore: number;
    avgRti30: number;
    highRate30: number;
    highCount30: number;
    slaBreaches30: number;
    breachRate30: number;
    total30: number;
  };
  readiness: {
    policyCoverage: boolean;
    responseSlaConfigured: boolean;
    queueWithinSla: boolean;
    activityCadenceHealthy: boolean;
  };
  auditEvents: Array<{
    id: string;
    actor_user_id: string | null;
    action: string;
    target_type: string | null;
    target_id: string | null;
    payload: Record<string, unknown> | null;
    created_at: string;
  }>;
};

export async function fetchEnterpriseWorkspaceObservability(
  accessToken: string,
  orgId: string,
): Promise<{ success: boolean; data?: EnterpriseWorkspaceObservability; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    const res = await fetch(`${BASE}/enterprise/workspace/observability?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, data: body as EnterpriseWorkspaceObservability };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function listUserEnterpriseOrgs(
  accessToken: string,
): Promise<{ success: boolean; organizations?: Array<EnterpriseOrgMembership>; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/orgs`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      organizations: (body as { organizations?: Array<EnterpriseOrgMembership> }).organizations || [],
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseBulkMemberSkip = {
  userId: string;
  reason: string;
  details?: Record<string, unknown>;
};

/** Owner/admin only — provisions multiple seats with the same policy checks as single-member upsert. */
export async function bulkUpsertEnterpriseMembers(
  orgId: string,
  input: { role: string; memberUserIds: string[] },
  accessToken: string | null,
): Promise<{
  success: boolean;
  requested?: number;
  upserted?: Array<{ userId: string; role: string; status: string }>;
  skipped?: EnterpriseBulkMemberSkip[];
  error?: string;
}> {
  if (!accessToken) return { success: false, error: 'no access token' };
  try {
    const res = await fetch(`${BASE}/enterprise/orgs/${encodeURIComponent(orgId)}/members/bulk`, {
      method: 'POST',
      headers: withJsonHeaders(accessToken),
      body: JSON.stringify(input),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { success: false, error: (body as { error?: string }).error || res.statusText };
    }
    const b = body as {
      success?: boolean;
      requested?: number;
      upserted?: Array<{ userId: string; role: string; status: string }>;
      skipped?: EnterpriseBulkMemberSkip[];
    };
    return {
      success: Boolean(b.success),
      requested: b.requested,
      upserted: b.upserted,
      skipped: b.skipped,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseOverview(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; metrics?: EnterpriseOverview; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/overview?orgId=${encodeURIComponent(orgId)}`, {
      headers: adminEnterpriseHeaders(adminSessionToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      metrics: (body as { metrics?: EnterpriseOverview }).metrics,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function provisionPilotOrganization(
  input: { name: string; slug?: string; industry?: string; sizeBand?: string },
  adminSessionToken: string | null,
): Promise<{ success: boolean; organization?: { id: string; name: string; slug: string; status: string }; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/provision-pilot`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify(input),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      organization: (body as { organization?: { id: string; name: string; slug: string; status: string } }).organization,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchAdminEnterpriseTenantSettings(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; tenant?: EnterpriseTenantSettings; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/tenant-settings?orgId=${encodeURIComponent(orgId)}`, {
      headers: adminEnterpriseHeaders(adminSessionToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, tenant: (body as { tenant?: EnterpriseTenantSettings }).tenant };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function updateAdminEnterpriseTenantSettings(
  payload: {
    orgId: string;
    organizationStatus?: string;
    industry?: string;
    sizeBand?: string;
    supportEmail?: string;
    customDomain?: string;
    seatLimit?: number | null;
    workspaceBadgeEnabled?: boolean;
    domainVerified?: boolean;
    allowedEmailDomains?: Array<string>;
    /** Partial overrides merged into tenant KV `segmentAlertPolicy`. */
    segmentAlertPolicy?: Partial<EnterpriseSegmentAlertPolicy>;
    /** High-tension response target (hours), clamped server-side. */
    responseSlaHours?: number | null;
    /** Digest email override stored in tenant KV (cron segment alerts + weekly board pack). */
    segmentAlertDigestEmail?: string | null;
    contractRenewalDate?: string | null;
    partnerWorkflowWebhookUrl?: string | null;
    partnerWebhookMinSeverity?: 'critical' | 'warning';
    partnerWorkflowSigningSecret?: string | null;
    partnerOpsAlertWebhookUrl?: string | null;
    partnerOpsAlertSigningSecret?: string | null;
    partnerSloAlertEmail?: string | null;
    partnerSloAlertMinSuccessPct?: number | null;
  },
  adminSessionToken: string | null,
): Promise<{ success: boolean; tenant?: EnterpriseTenantSettings; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/tenant-settings`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, tenant: (body as { tenant?: EnterpriseTenantSettings }).tenant };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function startEnterpriseDomainVerification(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  domainHost?: string;
  txtHost?: string;
  txtRecordValue?: string;
  tokenIssuedAt?: string;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/domain-verification/start`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      domainHost?: string;
      txtHost?: string;
      txtRecordValue?: string;
      tokenIssuedAt?: string;
    };
    return {
      success: Boolean(b.success),
      domainHost: b.domainHost,
      txtHost: b.txtHost,
      txtRecordValue: b.txtRecordValue,
      tokenIssuedAt: b.tokenIssuedAt,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function checkEnterpriseDomainVerification(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  matched?: boolean;
  txtHost?: string;
  expectedTxtValue?: string;
  recordsFound?: string[];
  lookupError?: string | null;
  tenant?: EnterpriseTenantSettings;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/domain-verification/check`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      matched?: boolean;
      txtHost?: string;
      expectedTxtValue?: string;
      recordsFound?: string[];
      lookupError?: string | null;
      tenant?: EnterpriseTenantSettings;
    };
    return {
      success: Boolean(b.success),
      matched: b.matched,
      txtHost: b.txtHost,
      expectedTxtValue: b.expectedTxtValue,
      recordsFound: b.recordsFound,
      lookupError: b.lookupError ?? null,
      tenant: b.tenant,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterprisePartnerWorkflowPreview = {
  webhookConfigured: boolean;
  partnerWebhookMinSeverity: 'critical' | 'warning';
  payload: unknown;
};

export async function fetchEnterprisePartnerWorkflowPreview(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; preview?: EnterprisePartnerWorkflowPreview; error?: string }> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/partner-workflow-preview?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      webhookConfigured?: boolean;
      partnerWebhookMinSeverity?: string;
      payload?: unknown;
    };
    if (!b.success) return { success: false, error: 'Preview failed' };
    const sev = b.partnerWebhookMinSeverity === 'warning' ? 'warning' : 'critical';
    return {
      success: true,
      preview: {
        webhookConfigured: Boolean(b.webhookConfigured),
        partnerWebhookMinSeverity: sev,
        payload: b.payload,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterprisePartnerWorkflowTestResult = {
  success: boolean;
  ok?: boolean;
  skipped?: boolean;
  reason?: string;
  httpStatus?: number;
  error?: string;
};

export type EnterprisePartnerWorkflowDeliveryEntry = {
  at: string;
  ok: boolean;
  attempts: number;
  httpStatus?: number;
  error?: string;
};

export async function fetchEnterprisePartnerWorkflowDeliveries(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  entries?: EnterprisePartnerWorkflowDeliveryEntry[];
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/partner-workflow-deliveries?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      entries?: EnterprisePartnerWorkflowDeliveryEntry[];
      updatedAt?: string | null;
    };
    if (!b.success) return { success: false, error: 'Deliveries failed' };
    return {
      success: true,
      entries: Array.isArray(b.entries) ? b.entries : [],
      updatedAt: b.updatedAt ?? null,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterprisePartnerDeadLetterEntry = {
  id: string;
  at: string;
  attempts: number;
  httpStatus?: number;
  error: string;
};

export async function fetchEnterprisePartnerDeadLetters(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  entries?: EnterprisePartnerDeadLetterEntry[];
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/partner-workflow-dead-letters?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      entries?: EnterprisePartnerDeadLetterEntry[];
      updatedAt?: string | null;
    };
    if (!b.success) return { success: false, error: 'Dead letters failed' };
    return {
      success: true,
      entries: Array.isArray(b.entries) ? b.entries : [],
      updatedAt: b.updatedAt ?? null,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterprisePartnerWorkflowReplayResult = {
  success?: boolean;
  ok?: boolean;
  skipped?: boolean;
  reason?: string;
  httpStatus?: number;
  error?: string;
};

export async function postEnterprisePartnerWorkflowReplay(
  orgId: string,
  deadLetterId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  removedDeadLetterId?: string;
  replay?: EnterprisePartnerWorkflowReplayResult;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/partner-workflow-replay`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, deadLetterId }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      removedDeadLetterId?: string;
      replay?: EnterprisePartnerWorkflowReplayResult;
      error?: string;
    };
    if (res.ok && body.success) {
      return {
        success: true,
        removedDeadLetterId: body.removedDeadLetterId,
        replay: body.replay,
      };
    }
    return {
      success: false,
      error: body.error || res.statusText,
      replay: body.replay,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterprisePartnerWorkflowSlo = {
  deadLetterOpenCount: number;
  escalationRecommended: boolean;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  deliveryAttemptsLast24h: { success: number; failed: number };
  deliveryAttemptsLast7d: { success: number; failed: number };
  successRate7dPct: number | null;
  generatedAt: string;
};

export type EnterprisePartnerApiKeyMetadata = {
  keyId: string;
  keyPrefix: string;
  createdAt: string;
  rotatedAt: string;
  useCount: number;
  lastUsedAt: string | null;
  ageDays: number | null;
  daysUntilExpiry: number | null;
  maxAgeDays: number;
  isExpired: boolean;
  revokedAt: string | null;
};

export type EnterprisePartnerApiKeyPolicy = {
  enabledByFlag: boolean;
  cutoffAt: string | null;
  cutoffPassed: boolean;
  effectiveEnabled: boolean;
};

export type EnterprisePartnerApiKeyMigrationRow = {
  orgId: string;
  orgSlug: string | null;
  orgName: string | null;
  keyId: string;
  keyPrefix: string;
  rotatedAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
  useCount: number;
  isExpired: boolean;
  ageDays: number | null;
  daysUntilExpiry: number | null;
  recencyBucket: 'never' | 'last_7d' | 'last_30d' | 'last_90d' | 'older';
  hasActiveKey: boolean;
};

export type EnterprisePartnerApiKeyMigrationReport = {
  generatedAt: string;
  totalOrganizationsWithPartnerKeyHistory: number;
  activeKeys: number;
  revokedKeys: number;
  expiredActiveKeys: number;
  neverUsedActiveKeys: number;
  recencyBuckets: {
    never: number;
    last7d: number;
    last30d: number;
    last90d: number;
    older: number;
  };
  rows: EnterprisePartnerApiKeyMigrationRow[];
};

export async function fetchAdminEnterprisePartnerApiKey(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; key?: EnterprisePartnerApiKeyMetadata | null; policy?: EnterprisePartnerApiKeyPolicy; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/partner-api-key?orgId=${encodeURIComponent(orgId)}`, {
      headers: adminEnterpriseHeaders(adminSessionToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const parsed = body as {
      success?: boolean;
      key?: EnterprisePartnerApiKeyMetadata | null;
      policy?: EnterprisePartnerApiKeyPolicy;
    };
    if (!parsed.success) return { success: false, error: 'Could not load partner API key status' };
    return { success: true, key: parsed.key ?? null, policy: parsed.policy };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function rotateAdminEnterprisePartnerApiKey(
  orgId: string,
  adminSessionToken: string | null,
  reason?: string,
): Promise<{ success: boolean; key?: EnterprisePartnerApiKeyMetadata; plaintextKey?: string; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/partner-api-key/rotate`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, reason: reason || null }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const parsed = body as { success?: boolean; key?: EnterprisePartnerApiKeyMetadata; plaintextKey?: string };
    if (!parsed.success || !parsed.key || !parsed.plaintextKey) return { success: false, error: 'Rotate failed' };
    return { success: true, key: parsed.key, plaintextKey: parsed.plaintextKey };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function revokeAdminEnterprisePartnerApiKey(
  orgId: string,
  adminSessionToken: string | null,
  reason?: string,
): Promise<{ success: boolean; key?: EnterprisePartnerApiKeyMetadata; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/partner-api-key/revoke`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, reason: reason || null }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const parsed = body as { success?: boolean; key?: EnterprisePartnerApiKeyMetadata };
    if (!parsed.success || !parsed.key) return { success: false, error: 'Revoke failed' };
    return { success: true, key: parsed.key };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchAdminEnterprisePartnerApiKeyMigrationReport(
  adminSessionToken: string | null,
  limit = 120,
): Promise<{ success: boolean; report?: EnterprisePartnerApiKeyMigrationReport; error?: string }> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/partner-api-key/migration-report?limit=${encodeURIComponent(String(limit))}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const parsed = body as { success?: boolean; rows?: EnterprisePartnerApiKeyMigrationRow[] } & EnterprisePartnerApiKeyMigrationReport;
    if (!parsed.success || !Array.isArray(parsed.rows)) {
      return { success: false, error: 'Could not load partner API migration report' };
    }
    return {
      success: true,
      report: {
        generatedAt: parsed.generatedAt,
        totalOrganizationsWithPartnerKeyHistory: Number(parsed.totalOrganizationsWithPartnerKeyHistory || 0),
        activeKeys: Number(parsed.activeKeys || 0),
        revokedKeys: Number(parsed.revokedKeys || 0),
        expiredActiveKeys: Number(parsed.expiredActiveKeys || 0),
        neverUsedActiveKeys: Number(parsed.neverUsedActiveKeys || 0),
        recencyBuckets: {
          never: Number(parsed.recencyBuckets?.never || 0),
          last7d: Number(parsed.recencyBuckets?.last7d || 0),
          last30d: Number(parsed.recencyBuckets?.last30d || 0),
          last90d: Number(parsed.recencyBuckets?.last90d || 0),
          older: Number(parsed.recencyBuckets?.older || 0),
        },
        rows: parsed.rows,
      },
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function pushAdminEnterprisePartnerApiKeyMigrationReminders(
  adminSessionToken: string | null,
  params?: { staleOnly?: boolean; limit?: number; forceRenotify?: boolean },
): Promise<{
  success: boolean;
  remindersPushed?: number;
  considered?: number;
  staleOnly?: boolean;
  forceRenotify?: boolean;
  skippedRecentlyReminded?: number;
  dedupeWindowHours?: number;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/partner-api-key/migration-report/reminders`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({
        staleOnly: params?.staleOnly ?? true,
        limit: params?.limit ?? 25,
        forceRenotify: params?.forceRenotify === true,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const parsed = body as {
      success?: boolean;
      remindersPushed?: number;
      considered?: number;
      staleOnly?: boolean;
      forceRenotify?: boolean;
      skippedRecentlyReminded?: number;
      dedupeWindowHours?: number;
    };
    if (!parsed.success) return { success: false, error: 'Could not push partner migration reminders' };
    return {
      success: true,
      remindersPushed: Number(parsed.remindersPushed || 0),
      considered: Number(parsed.considered || 0),
      staleOnly: Boolean(parsed.staleOnly),
      forceRenotify: Boolean(parsed.forceRenotify),
      skippedRecentlyReminded: Number(parsed.skippedRecentlyReminded || 0),
      dedupeWindowHours: Number(parsed.dedupeWindowHours || 24),
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterprisePartnerWorkflowSlo(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; slo?: EnterprisePartnerWorkflowSlo; error?: string }> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/partner-workflow-slo?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as { success?: boolean; slo?: EnterprisePartnerWorkflowSlo };
    if (!b.success || !b.slo) return { success: false, error: 'SLO snapshot failed' };
    return { success: true, slo: b.slo };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseMarketplaceSelections = Record<string, MarketplaceConnectorRollout>;

export async function fetchEnterpriseMarketplaceSelections(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  selections?: EnterpriseMarketplaceSelections;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/marketplace-selections?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      selections?: EnterpriseMarketplaceSelections;
      updatedAt?: string | null;
    };
    if (!b.success || !b.selections) return { success: false, error: 'Marketplace selections failed' };
    return { success: true, selections: b.selections, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseMarketplaceSelections(
  orgId: string,
  selections: EnterpriseMarketplaceSelections,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  selections?: EnterpriseMarketplaceSelections;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/marketplace-selections`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, selections }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      selections?: EnterpriseMarketplaceSelections;
      updatedAt?: string | null;
    };
    if (!b.success || !b.selections) return { success: false, error: 'Save failed' };
    return { success: true, selections: b.selections, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseCarrierPathways(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseCarrierPathwayProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/carrier-pathways?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseCarrierPathwayProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Carrier pathways failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseCarrierPathways(
  orgId: string,
  profile: EnterpriseCarrierPathwayProfile,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseCarrierPathwayProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/carrier-pathways`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, profile }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseCarrierPathwayProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Save failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseRegionPosture(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseRegionPostureProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/region-posture?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseRegionPostureProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Region posture load failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseRegionPosture(
  orgId: string,
  profile: EnterpriseRegionPostureProfile,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseRegionPostureProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/region-posture`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, profile }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseRegionPostureProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Save failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseTelemetryBaselines(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseTelemetryBaselinesProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/telemetry-baselines?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseTelemetryBaselinesProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Telemetry baselines load failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseTelemetryBaselines(
  orgId: string,
  profile: EnterpriseTelemetryBaselinesProfile,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseTelemetryBaselinesProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/telemetry-baselines`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, profile }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseTelemetryBaselinesProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Save failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseCostQuotaGuardrails(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseCostQuotaProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/cost-quota-guardrails?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseCostQuotaProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Cost & quota load failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseCostQuotaGuardrails(
  orgId: string,
  profile: EnterpriseCostQuotaProfile,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseCostQuotaProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/cost-quota-guardrails`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, profile }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseCostQuotaProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Save failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseAccountGrowthPriorities(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseAccountGrowthPrioritiesProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/account-growth-priorities?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseAccountGrowthPrioritiesProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Account growth priorities load failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseAccountGrowthPriorities(
  orgId: string,
  profile: EnterpriseAccountGrowthPrioritiesProfile,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  profile?: EnterpriseAccountGrowthPrioritiesProfile;
  updatedAt?: string | null;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/account-growth-priorities`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, profile }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    const b = body as {
      success?: boolean;
      profile?: EnterpriseAccountGrowthPrioritiesProfile;
      updatedAt?: string | null;
    };
    if (!b.success || !b.profile) return { success: false, error: 'Save failed' };
    return { success: true, profile: b.profile, updatedAt: b.updatedAt ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function postEnterprisePartnerWorkflowTest(
  orgId: string,
  adminSessionToken: string | null,
): Promise<EnterprisePartnerWorkflowTestResult> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/partner-workflow-test`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId }),
    });
    const body = (await res.json().catch(() => ({}))) as EnterprisePartnerWorkflowTestResult & { error?: string };
    if (!res.ok) {
      return { success: false, error: body.error || res.statusText };
    }
    return body;
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseWaveProgress(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; workstreamCompleted?: Record<string, boolean>; updatedAt?: string | null; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/wave-progress?orgId=${encodeURIComponent(orgId)}`, {
      headers: adminEnterpriseHeaders(adminSessionToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      workstreamCompleted: (body as { workstreamCompleted?: Record<string, boolean> }).workstreamCompleted ?? {},
      updatedAt: (body as { updatedAt?: string | null }).updatedAt ?? null,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseWaveProgress(
  orgId: string,
  workstreamCompleted: Record<string, boolean>,
  adminSessionToken: string | null,
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/wave-progress`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, workstreamCompleted }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseWave2Module = {
  id: string;
  status: string;
  summary: string;
};

export type EnterpriseWave2MetricsResponse = {
  success: boolean;
  wave: number;
  phaseTitle: string;
  preview: boolean;
  generatedAt: string;
  organization: {
    id: string;
    name: string;
    slug: string;
    status: string;
    industry: string | null;
    sizeBand: string | null;
  };
  metricsEcho: EnterpriseOverview;
  cohortShare30d: { stablePct: number; elasticPct: number; highTensionPct: number } | null;
  modules: EnterpriseWave2Module[];
};

export async function fetchEnterpriseWave2Metrics(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; data?: EnterpriseWave2MetricsResponse; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/wave2-metrics?orgId=${encodeURIComponent(orgId)}`, {
      headers: adminEnterpriseHeaders(adminSessionToken),
    });
    const body = (await res.json().catch(() => ({}))) as EnterpriseWave2MetricsResponse & { error?: string };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return { success: true, data: body };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseWave4OutcomeResponse = {
  success: boolean;
  wave: number;
  phaseTitle: string;
  preview: boolean;
  generatedAt: string;
  organization: {
    id: string;
    name: string;
    slug: string;
    status: string;
    industry: string | null;
    sizeBand: string | null;
  };
  metricsEcho: EnterpriseOverview;
  attribution: OutcomeAttributionPreview;
  /** Client-supplied aggregate proxies when saved (Phase 4 bridge). Present after Edge deploy. */
  outcomeBridge?: EnterpriseOutcomeBridgeStored | null;
  correlationNarrative?: string[];
  /** Newest-first; capped on server (see wave4-outcome-preview). */
  outcomeBridgeSnapshots?: OutcomeBridgeSnapshotRecord[];
  modules: EnterpriseWave2Module[];
};

export async function fetchEnterpriseWave4OutcomePreview(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; data?: EnterpriseWave4OutcomeResponse; error?: string }> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/wave4-outcome-preview?orgId=${encodeURIComponent(orgId)}`,
      {
        headers: adminEnterpriseHeaders(adminSessionToken),
      },
    );
    const body = (await res.json().catch(() => ({}))) as EnterpriseWave4OutcomeResponse & { error?: string };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return { success: true, data: body };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/** Seat utilization + invitation health signals (admin). */
export async function fetchEnterpriseSeatEngagementPreview(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  generatedAt?: string;
  summary?: EnterpriseSeatEngagementSummary;
  signals?: EnterpriseSeatEngagementSignal[];
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/seat-engagement-preview?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      generatedAt?: string;
      summary?: EnterpriseSeatEngagementSummary;
      signals?: EnterpriseSeatEngagementSignal[];
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return {
      success: Boolean(body.success),
      generatedAt: body.generatedAt,
      summary: body.summary,
      signals: body.signals,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/** Renewal timing, utilization, and segment-risk narrative (admin). */
export async function fetchEnterpriseRenewalExpansionPreview(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  generatedAt?: string;
  summary?: EnterpriseRenewalExpansionSummary;
  signals?: EnterpriseRenewalExpansionSignal[];
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/renewal-expansion-preview?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      generatedAt?: string;
      summary?: EnterpriseRenewalExpansionSummary;
      signals?: EnterpriseRenewalExpansionSignal[];
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return {
      success: Boolean(body.success),
      generatedAt: body.generatedAt,
      summary: body.summary,
      signals: body.signals,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseOutcomeBridge(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; outcomeBridge?: EnterpriseOutcomeBridgeStored | null; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/outcome-bridge?orgId=${encodeURIComponent(orgId)}`, {
      headers: adminEnterpriseHeaders(adminSessionToken),
    });
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      outcomeBridge?: EnterpriseOutcomeBridgeStored | null;
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return { success: true, outcomeBridge: body.outcomeBridge ?? null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseOutcomeBridge(
  orgId: string,
  payload: {
    eapOrWellbeingUtilizationPct30d: number | null;
    absenteeismDaysPerFte30d: number | null;
    notes: string | null;
  },
  adminSessionToken: string | null,
): Promise<{ success: boolean; outcomeBridge?: EnterpriseOutcomeBridgeStored; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/outcome-bridge`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, ...payload }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      outcomeBridge?: EnterpriseOutcomeBridgeStored;
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return { success: true, outcomeBridge: body.outcomeBridge };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseOutcomeBridgeSnapshots(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; snapshots?: OutcomeBridgeSnapshotRecord[]; error?: string }> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/outcome-bridge-snapshots?orgId=${encodeURIComponent(orgId)}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      snapshots?: OutcomeBridgeSnapshotRecord[];
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return { success: true, snapshots: body.snapshots ?? [] };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function recordEnterpriseOutcomeBridgeSnapshot(
  orgId: string,
  opts: { label?: string | null; includeMetricsEcho?: boolean },
  adminSessionToken: string | null,
): Promise<{ success: boolean; snapshots?: OutcomeBridgeSnapshotRecord[]; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/outcome-bridge-snapshot`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({
        orgId,
        label: opts.label ?? undefined,
        includeMetricsEcho: opts.includeMetricsEcho !== false,
      }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      snapshots?: OutcomeBridgeSnapshotRecord[];
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return { success: true, snapshots: body.snapshots };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function ingestEnterpriseOutcomeBridgeRows(
  orgId: string,
  rows: Array<{
    periodLabel?: string | null;
    eapOrWellbeingUtilizationPct30d?: number | null;
    absenteeismDaysPerFte30d?: number | null;
    notes?: string | null;
  }>,
  applyLatestAsCurrentBridge: boolean,
  adminSessionToken: string | null,
): Promise<{ success: boolean; ingested?: number; snapshots?: OutcomeBridgeSnapshotRecord[]; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/outcome-bridge-ingest`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, rows, applyLatestAsCurrentBridge }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      ingested?: number;
      snapshots?: OutcomeBridgeSnapshotRecord[];
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return { success: true, ingested: body.ingested, snapshots: body.snapshots };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function deleteEnterpriseOutcomeBridgeSnapshot(
  orgId: string,
  snapshotId: string,
  adminSessionToken: string | null,
): Promise<{ success: boolean; snapshots?: OutcomeBridgeSnapshotRecord[]; error?: string }> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/outcome-bridge-snapshot-delete`, {
      method: 'POST',
      headers: adminEnterpriseHeaders(adminSessionToken),
      body: JSON.stringify({ orgId, snapshotId }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      snapshots?: OutcomeBridgeSnapshotRecord[];
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return { success: true, snapshots: body.snapshots };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseAuditEventRow = {
  id: string;
  action: string;
  target_type: string;
  target_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
  actor_user_id: string | null;
};

export async function fetchEnterpriseAuditEvents(
  orgId: string,
  adminSessionToken: string | null,
  limit = 80,
): Promise<{
  success: boolean;
  events?: EnterpriseAuditEventRow[];
  generatedAt?: string;
  error?: string;
}> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/audit-events?orgId=${encodeURIComponent(orgId)}&limit=${encodeURIComponent(String(limit))}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      events?: EnterpriseAuditEventRow[];
      generatedAt?: string;
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return { success: true, events: body.events ?? [], generatedAt: body.generatedAt };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseInterventionQueueItem = {
  userId: string;
  segment: string;
  signal: string;
  lastRti: number;
  lastOccurredAt: string;
  status: string;
  source: string;
};

/** Per-user intervention candidates from org check_ins (30d). Requires admin session or unified admin JWT. */
export async function fetchEnterpriseSegmentAlerts(
  orgId: string,
  adminSessionToken: string | null,
): Promise<{
  success: boolean;
  alerts?: EnterpriseSegmentAlertItem[];
  policy?: EnterpriseSegmentAlertPolicy;
  generatedAt?: string;
  error?: string;
}> {
  try {
    const res = await fetch(`${BASE}/admin/enterprise/segment-alerts?orgId=${encodeURIComponent(orgId)}`, {
      headers: adminEnterpriseHeaders(adminSessionToken),
    });
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      alerts?: EnterpriseSegmentAlertItem[];
      policy?: EnterpriseSegmentAlertPolicy;
      generatedAt?: string;
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return {
      success: true,
      alerts: Array.isArray(body.alerts) ? body.alerts : [],
      policy: body.policy,
      generatedAt: typeof body.generatedAt === 'string' ? body.generatedAt : undefined,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseInterventionQueue(
  orgId: string,
  adminSessionToken: string | null,
  limit = 40,
): Promise<{ success: boolean; items?: EnterpriseInterventionQueueItem[]; generatedAt?: string; error?: string }> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/intervention-queue?orgId=${encodeURIComponent(orgId)}&limit=${encodeURIComponent(String(limit))}`,
      { headers: adminEnterpriseHeaders(adminSessionToken) },
    );
    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      items?: EnterpriseInterventionQueueItem[];
      generatedAt?: string;
      error?: string;
    };
    if (!res.ok) return { success: false, error: body.error || res.statusText };
    return {
      success: true,
      items: Array.isArray(body.items) ? body.items : [],
      generatedAt: typeof body.generatedAt === 'string' ? body.generatedAt : undefined,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function listAdminEnterpriseSeats(
  orgId: string,
  adminSessionToken: string | null,
  limit = 200,
): Promise<{ success: boolean; seats?: Array<EnterpriseSeatMember>; summary?: { active: number; pending: number; suspended: number; seatLimit: number | null }; error?: string }> {
  try {
    const res = await fetch(
      `${BASE}/admin/enterprise/seats?orgId=${encodeURIComponent(orgId)}&limit=${encodeURIComponent(String(limit))}`,
      {
        headers: adminEnterpriseHeaders(adminSessionToken),
      },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      seats: (body as { seats?: Array<EnterpriseSeatMember> }).seats || [],
      summary: (body as { summary?: { active: number; pending: number; suspended: number; seatLimit: number | null } }).summary,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function listIdentityProviders(
  orgId: string,
  accessToken: string,
): Promise<{ success: boolean; providers?: Array<Record<string, unknown>>; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/orgs/${encodeURIComponent(orgId)}/identity-provider`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, providers: (body as { providers?: Array<Record<string, unknown>> }).providers || [] };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function upsertIdentityProvider(
  orgId: string,
  payload: {
    providerType: 'saml' | 'oidc';
    status?: 'draft' | 'active' | 'disabled';
    issuer?: string;
    audience?: string;
    discoveryUrl?: string;
    metadataUrl?: string;
    config?: Record<string, unknown>;
  },
  accessToken: string,
): Promise<{ success: boolean; provider?: Record<string, unknown>; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/orgs/${encodeURIComponent(orgId)}/identity-provider`, {
      method: 'POST',
      headers: withJsonHeaders(accessToken),
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, provider: (body as { provider?: Record<string, unknown> }).provider };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function getBrandingProfile(
  orgId: string,
  accessToken: string,
): Promise<{ success: boolean; branding?: Record<string, unknown> | null; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/orgs/${encodeURIComponent(orgId)}/branding`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, branding: (body as { branding?: Record<string, unknown> | null }).branding || null };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function upsertBrandingProfile(
  orgId: string,
  payload: {
    brandName?: string;
    logoUrl?: string;
    supportEmail?: string;
    primaryColor?: string;
    accentColor?: string;
    customDomain?: string;
    config?: Record<string, unknown>;
  },
  accessToken: string,
): Promise<{ success: boolean; branding?: Record<string, unknown>; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/orgs/${encodeURIComponent(orgId)}/branding`, {
      method: 'POST',
      headers: withJsonHeaders(accessToken),
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, branding: (body as { branding?: Record<string, unknown> }).branding };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseWorkEngineHealth = {
  success: boolean;
  engine: string;
  modules: {
    integrations: string;
    aiMiddleware: string;
  };
  generatedAt: string;
};

export type EnterpriseConnectorConfig = {
  enabled: boolean;
  mode: 'mock' | 'live';
  health: 'healthy' | 'degraded' | 'offline';
  lastSyncAt: string | null;
  updatedAt: string;
  settings: Record<string, unknown>;
};

export type EnterpriseIntegrationConnector = {
  id: string;
  label: string;
  domain: 'hris' | 'payroll' | 'engagement' | 'identity';
  authMode: 'api_key' | 'oauth2' | 'service_account';
  supportsMock: boolean;
  config: EnterpriseConnectorConfig;
};

export type EnterpriseIntegrationSyncJob = {
  id: string;
  connectorId: string;
  organizationId: string;
  mode: 'mock' | 'live';
  status: 'succeeded' | 'failed' | 'running';
  recordsRead: number;
  recordsWritten: number;
  startedAt: string;
  completedAt: string;
  initiatedBy: string;
};

export async function fetchEnterpriseWorkEngineHealth(
  accessToken: string,
): Promise<{ success: boolean; health?: EnterpriseWorkEngineHealth; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/engine/health`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, health: body as EnterpriseWorkEngineHealth };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function listEnterpriseIntegrationConnectors(
  accessToken: string,
  orgId: string,
): Promise<{ success: boolean; connectors?: EnterpriseIntegrationConnector[]; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    const res = await fetch(`${BASE}/enterprise/integrations/connectors?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      connectors: (body as { connectors?: EnterpriseIntegrationConnector[] }).connectors || [],
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function configureEnterpriseIntegrationConnector(
  accessToken: string,
  payload: {
    organizationId: string;
    connectorId: string;
    enabled?: boolean;
    mode?: 'mock' | 'live';
    health?: 'healthy' | 'degraded' | 'offline';
    settings?: Record<string, unknown>;
  },
): Promise<{ success: boolean; config?: EnterpriseConnectorConfig; error?: string }> {
  try {
    const res = await fetch(
      `${BASE}/enterprise/integrations/connectors/${encodeURIComponent(payload.connectorId)}/configure`,
      {
        method: 'POST',
        headers: withJsonHeaders(accessToken),
        body: JSON.stringify({
          organizationId: payload.organizationId,
          enabled: payload.enabled,
          mode: payload.mode,
          health: payload.health,
          settings: payload.settings,
        }),
      },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return {
      success: true,
      config: (body as { config?: EnterpriseConnectorConfig }).config,
    };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function runEnterpriseIntegrationSyncJob(
  accessToken: string,
  payload: {
    organizationId: string;
    connectorId: string;
    durationMs?: number;
    recordsRead?: number;
    recordsWritten?: number;
  },
): Promise<{ success: boolean; job?: EnterpriseIntegrationSyncJob; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/integrations/sync-jobs`, {
      method: 'POST',
      headers: withJsonHeaders(accessToken),
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, job: (body as { job?: EnterpriseIntegrationSyncJob }).job };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function listEnterpriseIntegrationSyncJobs(
  accessToken: string,
  orgId: string,
): Promise<{ success: boolean; jobs?: EnterpriseIntegrationSyncJob[]; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    const res = await fetch(`${BASE}/enterprise/integrations/sync-jobs?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, jobs: (body as { jobs?: EnterpriseIntegrationSyncJob[] }).jobs || [] };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseAiMiddlewareResult = {
  success: boolean;
  middleware?: {
    provider: string;
    rationale: string;
    guardrails: {
      piiFindings: number;
      redactionApplied: boolean;
    };
    redactedPrompt: string;
  };
  output?: {
    kind: string;
    text: string;
  };
  error?: string;
};

export async function evaluateEnterpriseAiMiddleware(
  accessToken: string,
  payload: {
    organizationId: string;
    prompt: string;
  },
): Promise<EnterpriseAiMiddlewareResult> {
  try {
    const res = await fetch(`${BASE}/enterprise/ai/middleware/evaluate`, {
      method: 'POST',
      headers: withJsonHeaders(accessToken),
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return body as EnterpriseAiMiddlewareResult;
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseRbacCapabilities = {
  success: boolean;
  role?: string;
  status?: string;
  capabilities?: string[];
  error?: string;
};

export async function fetchEnterpriseRbacCapabilities(
  accessToken: string,
  orgId: string,
): Promise<EnterpriseRbacCapabilities> {
  try {
    const res = await fetch(`${BASE}/enterprise/orgs/${encodeURIComponent(orgId)}/rbac/capabilities`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return body as EnterpriseRbacCapabilities;
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export type EnterpriseGovernanceAuditSummary = {
  success: boolean;
  days: number;
  totalEvents: number;
  actionCounts: Record<string, number>;
  actorCounts: Record<string, number>;
  recentEvents: Array<{
    id: string;
    action: string;
    target_type: string | null;
    target_id: string | null;
    created_at: string;
    actor_user_id: string | null;
  }>;
  error?: string;
};

export async function fetchEnterpriseGovernanceAuditSummary(
  accessToken: string,
  orgId: string,
  days = 30,
): Promise<EnterpriseGovernanceAuditSummary> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    params.set('days', String(days));
    const res = await fetch(`${BASE}/enterprise/governance/audit-summary?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        days,
        totalEvents: 0,
        actionCounts: {},
        actorCounts: {},
        recentEvents: [],
        error: (body as { error?: string }).error || res.statusText,
      };
    }
    return body as EnterpriseGovernanceAuditSummary;
  } catch (error) {
    return {
      success: false,
      days,
      totalEvents: 0,
      actionCounts: {},
      actorCounts: {},
      recentEvents: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export type EnterpriseGovernancePolicy = {
  retentionDays: number;
  allowExports: boolean;
  piiRedactionMode: 'strict' | 'balanced';
  updatedAt: string;
};

export async function fetchEnterpriseGovernancePolicy(
  accessToken: string,
  orgId: string,
): Promise<{ success: boolean; policy?: EnterpriseGovernancePolicy; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.set('organizationId', orgId);
    const res = await fetch(`${BASE}/enterprise/governance/policy?${params.toString()}`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, policy: (body as { policy?: EnterpriseGovernancePolicy }).policy };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseGovernancePolicy(
  accessToken: string,
  payload: {
    organizationId: string;
    retentionDays?: number;
    allowExports?: boolean;
    piiRedactionMode?: 'strict' | 'balanced';
  },
): Promise<{ success: boolean; policy?: EnterpriseGovernancePolicy; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/governance/policy`, {
      method: 'POST',
      headers: withJsonHeaders(accessToken),
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, policy: (body as { policy?: EnterpriseGovernancePolicy }).policy };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function fetchEnterpriseIdentityClaimMapping(
  accessToken: string,
  orgId: string,
): Promise<{ success: boolean; mapping?: Record<string, string>; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/orgs/${encodeURIComponent(orgId)}/identity/claim-mapping`, {
      headers: withJsonHeaders(accessToken),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, mapping: (body as { mapping?: Record<string, string> }).mapping || {} };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export async function saveEnterpriseIdentityClaimMapping(
  accessToken: string,
  payload: {
    orgId: string;
    mapping: Record<string, string>;
  },
): Promise<{ success: boolean; mapping?: Record<string, string>; error?: string }> {
  try {
    const res = await fetch(`${BASE}/enterprise/orgs/${encodeURIComponent(payload.orgId)}/identity/claim-mapping`, {
      method: 'POST',
      headers: withJsonHeaders(accessToken),
      body: JSON.stringify({ mapping: payload.mapping }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: (body as { error?: string }).error || res.statusText };
    return { success: true, mapping: (body as { mapping?: Record<string, string> }).mapping || {} };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}
